import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { siteInformationService } from "../../services/siteInformationService";
import { cloneFiles } from "./fileSlice.js";
import { fetchOrQueue } from "../../lib/offlineQueue.js";
import { getCachedData, putCachedData } from "../../lib/offlineDb.js";
import { siteSyncDetails } from "../../utils/siteSyncDetails.js";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8005/api";

/**
 * Async thunk to fetch site overview data for a specific report
 */
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const fetchSiteOverviewData = createAsyncThunk(
  "siteOverviewData/fetchByReport",
  async ({ siteId, reportId }, { rejectWithValue }) => {
    const cacheKey = `site-overview:${siteId}:${reportId}`;
    try {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        const cached = await getCachedData(cacheKey);
        if (cached) return cached;
      }
      const response = await siteInformationService.getByReport(siteId, reportId);
      const payload = response?.data || response;
      const normalized = payload?.data || payload;
      await putCachedData(cacheKey, normalized);
      return normalized;
    } catch (error) {
      const cached = await getCachedData(cacheKey).catch(() => null);
      if (cached) return cached;
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

/**
 * Async thunk to update site overview data
 */
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, { siteId: string; reportId?: string; payload: object }, {}>} */
export const updateSiteOverviewData = createAsyncThunk(
  "siteOverviewData/update",
  async ({ siteId, reportId, payload }, { rejectWithValue, dispatch, getState }) => {
    try {
      const reportData =
        getState()?.auditReports?.allReportsData?.[reportId] ||
        getState()?.auditReports?.currentReportData;
      const pendingSectionFiles = (reportData?.files || []).filter(
        (file) =>
          file?.isImported &&
          file?.sourceFileId &&
          file?.metadata?.importScope === "section" &&
          file?.relatedTo?.entityType !== "work" &&
          file?.relatedTo?.entityType !== "observation" &&
          !String(file?.relatedTo?.entityId || "").startsWith("imported-"),
      );
      const cloneFilesPromise =
        pendingSectionFiles.length > 0
          ? dispatch(
              cloneFiles({
                files: pendingSectionFiles,
                siteId,
                reportId,
              }),
            ).unwrap()
          : Promise.resolve([]);

      const requestUrl = `${API_BASE_URL}/site-information/${siteId}/${reportId}`;
      const requestHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      const requestBody = JSON.stringify(payload);
      const siteDetails = siteSyncDetails({ meta: { entityType: "site-overview", siteId, reportId }, body: requestBody }, getState());

      // Phase 2: if the device is offline, queue this save in the outbox
      // instead of failing — the auditor's edits are kept and applied
      // optimistically, and the request is replayed once reconnected.
      const [outcome] = await Promise.all([
        fetchOrQueue({
          dispatch,
          requestInit: { url: requestUrl, method: "PUT", headers: requestHeaders, body: requestBody },
          meta: { description: siteDetails.detail, siteName: siteDetails.siteName, siteId, reportId,
            entityType: "site-overview", entityId: reportId },
          performRequest: async () => {
            const response = await siteInformationService.upsertByReport(siteId, reportId, payload);
            return response?.data || response;
          },
        }),
        cloneFilesPromise,
      ]);

      if (outcome.queued) {
        return { ...(reportData?.siteOverview || {}), ...payload, siteId, reportId, _offlineQueued: true };
      }

      const result = outcome.result;
      const normalizedPayload = result?.data || result;

      return normalizedPayload;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  currentSiteOverview: null,
  loading: false,
  error: null,
  lastUpdated: null,
};

const siteOverviewDataSlice = createSlice({
  name: "siteOverviewData",
  initialState,
  reducers: {
    /**
     * Manually set the current site overview data
     */
    setCurrentSiteOverview: (state, action) => {
      state.currentSiteOverview = action.payload;
      state.lastUpdated = new Date().toISOString();
    },
    /**
     * Manually update a specific field in the current site overview
     */
    updateSiteOverviewField: (state, action) => {
      if (state.currentSiteOverview) {
        const { field, value } = action.payload;
        state.currentSiteOverview[field] = value;
        state.lastUpdated = new Date().toISOString();
      }
    },
    /**
     * Clear the current site overview data
     */
    clearCurrentSiteOverview: (state) => {
      state.currentSiteOverview = null;
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchSiteOverviewData
      .addCase(fetchSiteOverviewData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSiteOverviewData.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSiteOverview = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchSiteOverviewData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // updateSiteOverviewData
      .addCase(updateSiteOverviewData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSiteOverviewData.fulfilled, (state, action) => {
        state.loading = false;
        state.currentSiteOverview = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateSiteOverviewData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setCurrentSiteOverview,
  updateSiteOverviewField,
  clearCurrentSiteOverview,
} = siteOverviewDataSlice.actions;

/**
 * Selector to get the current site overview data
 */
export const selectCurrentSiteOverview = (state) => state.siteOverviewData?.currentSiteOverview;

/**
 * Selector to get specific field from current site overview
 */
export const selectSiteOverviewField = (fieldName) => (state) =>
  state.siteOverviewData?.currentSiteOverview?.[fieldName];

/**
 * Selector to get the loading state
 */
export const selectSiteOverviewLoading = (state) => state.siteOverviewData?.loading;

/**
 * Selector to get the error state
 */
export const selectSiteOverviewError = (state) => state.siteOverviewData?.error;

export default siteOverviewDataSlice.reducer;
